import { useListFlows } from "#hooks/adapters/useFlows";
import { FLOW_ATTRIBUTES, useFlowHeaders } from "#hooks/tables/useFlowHeaders";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import useLocalization from "#src/hooks/useLocalization";
import {
  DataTablePanel,
  ItemAction,
  StorageKeys,
  useFilters,
} from "@validereinc/common-components";
import type { AssetGroupAssetType } from "@validereinc/domain";
import { AssetType, SortDirection } from "@validereinc/domain";
import { toFlattenedObject } from "@validereinc/utilities";
import React from "react";

export const NetworkFlowsTablePanel = ({
  filterConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
  selectedFlows,
  setSelectedFlows,
}: Pick<
  StorageKeys,
  "filterConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
> & {
  selectedFlows: Record<string, AssetGroupAssetType>;
  setSelectedFlows: (newFlows: Record<string, AssetGroupAssetType>) => void;
}) => {
  const { localize } = useLocalization();
  const [filters] = useFilters(filterConfigStorageKey);

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const { data: flows, isLoading } = useListFlows({
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: toFlattenedObject(filters),
  });
  const { isLoading: isLoadingFlowHeaders, headers: flowHeaders } =
    useFlowHeaders<AssetGroupAssetType>();

  const items =
    flows?.data?.map((flow) => ({ ...flow, asset_type: AssetType.FLOW })) ?? [];

  const addItem = (item: AssetGroupAssetType) => {
    return selectedFlows?.[item.id]
      ? null
      : setSelectedFlows({
          ...selectedFlows,
          [item.id]: { ...item, asset_type: AssetType.FLOW },
        });
  };

  const getItemActions = ({
    item,
  }: {
    item: AssetGroupAssetType;
  }): ItemAction[] => {
    return [
      {
        label: "Add",
        buttonProps: {
          onClick: () => addItem(item),
          icon: "plus-circle",
        },
      },
    ];
  };

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{
        title: localize("flow_plural"),
      }}
      dataTableProps={{
        headers: flowHeaders.filter(
          ({ key }) => ![FLOW_ATTRIBUTES.PRODUCT_TYPE.key].includes(key)
        ),
        isLoading: isLoading || isLoadingFlowHeaders,
        getItemId: (item) => item?.id,
        items,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: flows?.total_entries,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
        isFluid: false,
        selected: selectedFlows,
        onSelectionChange: setSelectedFlows,
        getItemActions,
      }}
    />
  );
};
